/**
 * Canonical date utility functions.
 * Extracted from inline implementations across ~17 source files.
 * No behavior change — pure consolidation.
 */

/**
 * Parses a period string into a YYYYMM integer.
 * Accepts MM/YYYY and YYYY-MM formats. Returns 0 for unparseable.
 * No Date-object fallback.
 * @param {*} value
 * @returns {number} YYYYMM integer
 */
export function toYearMonth(value) {
  const txt = String(value || '').trim()
  const slash = txt.match(/^(\d{2})\/(\d{4})$/)
  if (slash) {
    const m = Number(slash[1])
    if (m < 1 || m > 12) return 0
    return Number(`${slash[2]}${slash[1]}`)
  }
  // NOTE: no $ anchor here — existing code passes ISO date strings
  // like '2026-04-01' and expects '202604' as result
  const iso = txt.match(/^(\d{4})-(\d{2})/)
  if (iso) {
    const m = Number(iso[2])
    if (m < 1 || m > 12) return 0
    return Number(`${iso[1]}${iso[2]}`)
  }
  return 0
}

/**
 * Normalizes a period string to YYYY-MM format.
 * Returns empty string for unparseable inputs.
 * @param {*} value
 * @returns {string} YYYY-MM or ''
 */
export function monthKey(value) {
  const txt = String(value || '').trim()
  const slash = txt.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return `${slash[2]}-${slash[1]}`
  // NOTE: no $ anchor — existing code may pass ISO date strings
  const iso = txt.match(/^(\d{4})-(\d{2})/)
  if (iso) return iso[0]
  return ''
}

/**
 * Computes signed month difference between two YYYYMM values.
 * Returns 0 for invalid inputs.
 * @param {number} a YYYYMM
 * @param {number} b YYYYMM
 * @returns {number} signed month diff
 */
export function monthDiff(a, b) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0
  const ay = Math.floor(a / 100)
  const am = a % 100
  const by = Math.floor(b / 100)
  const bm = b % 100
  if (am < 1 || am > 12 || bm < 1 || bm > 12) return 0
  return (ay - by) * 12 + (am - bm)
}

/**
 * Converts a Date object (or ISO string) to YYYY-MM period key.
 * Returns null for invalid dates.
 * @param {Date|string|null} date
 * @returns {string|null}
 */
export function fechaToPeriodo(date) {
  if (date == null) return null
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Parses a YYYY-MM or MM/YYYY period key to a Date (first of month).
 * Returns null for invalid keys.
 * @param {string|null} key
 * @returns {Date|null}
 */
export function periodoToDate(key) {
  if (!key) return null
  const txt = String(key).trim()
  const slash = txt.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return new Date(Number(slash[2]), Number(slash[1]) - 1, 1)
  const iso = txt.match(/^(\d{4})-(\d{2})$/)
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, 1)
  const raw = new Date(txt)
  if (Number.isNaN(raw.getTime())) return null
  return new Date(raw.getFullYear(), raw.getMonth(), 1)
}

/**
 * Builds an inclusive array of YYYY-MM keys from fromKey to toKey.
 * Uses monthKey internally for normalization.
 * Returns [] for invalid keys or when fromKey > toKey.
 * @param {string} fromKey YYYY-MM or MM/YYYY
 * @param {string} toKey YYYY-MM or MM/YYYY
 * @returns {string[]}
 */
export function buildMonthKeysInRange(fromKey, toKey) {
  const from = monthKey(fromKey)
  const to = monthKey(toKey)
  if (!from || !to) return []
  const fromDate = periodoToDate(from)
  const toDate = periodoToDate(to)
  if (!fromDate || !toDate || fromDate > toDate) return []
  const out = []
  const d = new Date(fromDate)
  while (d <= toDate) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
    d.setMonth(d.getMonth() + 1)
  }
  return out
}
